import { ParseKeys } from "i18next";
import { PropsWithChildren, ReactElement, useCallback, useContext, useMemo, useState } from "react";
import { ErrorBoundary, ErrorBoundaryProps } from "react-error-boundary";
import { ErrorBoundaryInfoContextValue, ErrorDisplayInfo, ErrorInfoContext } from "./ComponentErrorBoundary.ErrorInfoContext";
import ErrorFallback from "./ComponentErrorBoundary.Fallback";

export interface ComponentErrorBoundaryProps extends Pick<ErrorBoundaryProps, "onError"> {
  componentName?: string;
  componentNameTKey?: ParseKeys<"translation">;
  displayInfo?: ErrorDisplayInfo[];
  allowPageRefresh?: boolean;
}

export default function ComponentErrorBoundary({ children, ...props }: PropsWithChildren<ComponentErrorBoundaryProps>): ReactElement {
  const parentErrorInfoContext = useContext(ErrorInfoContext) as ErrorBoundaryInfoContextValue | undefined; // top level boundary will not have a parent context

  const [contextInfo, setContextInfo] = useState<ErrorBoundaryInfoContextValue["info"]>({});

  const aggregatedContextInfo = useMemo<ErrorBoundaryInfoContextValue["info"]>(
    () => ({
      ...props.displayInfo ? Object.fromEntries(props.displayInfo.map(({ key, value }) => [key, value])) : {},
      ...contextInfo,
      ...parentErrorInfoContext?.info ?? {},
    }),
    [contextInfo, parentErrorInfoContext?.info, props.displayInfo],
  );

  const addOrUpdateInfo = useCallback<ErrorBoundaryInfoContextValue["addOrUpdateInfo"]>(
    info => setContextInfo(prevInfo => ({ ...prevInfo, [info.key]: info.value })),
    [],
  );

  const contextValue = useMemo<ErrorBoundaryInfoContextValue>(
    () => ({
      info: aggregatedContextInfo,
      addOrUpdateInfo,
    }),
    [addOrUpdateInfo, aggregatedContextInfo],
  );

  return (
    <ErrorBoundary fallbackRender={fallBackProps => <ErrorFallback {...fallBackProps} {...props} contextInfo={aggregatedContextInfo} />}>
      <ErrorInfoContext.Provider value={contextValue}>
        {children}
      </ErrorInfoContext.Provider>
    </ErrorBoundary>
  );
}
